import {
  ResponsiveChartContainer,
  LinePlot,
  BarPlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsReferenceLine,
  CardinalDirections,
  axisClasses,
  chartsGridClasses,
  ChartsGrid,
  ChartsTooltip,
  MarkPlot,
  AllSeriesType,
} from "@mui/x-charts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, overview } from "../../../../../i18n";
import React from "react";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";

export const baseLineStrokeWidth = 1;

interface Props {
  series: AllSeriesType[];
  labels: string[];
  showBaseLine: boolean;
}

export const YearByYearChart = ({ series, labels, showBaseLine }: Props) => {
  const { t } = useTranslation([common, overview], { nsMode: "fallback" });

  const chartHeight = 290;

  const margin: Partial<CardinalDirections<number>> = {
    top: 40,
    bottom: 30,
    left: 100,
    right: 10,
  };

  const theme = useTheme();

  const lg = useMediaQuery(theme.breakpoints.up("lg"));

  const width: number | undefined = lg ? undefined : 980;

  const baseLine = 1;

  return (
    <Box sx={{ position: "relative", width: "100%", overflow: "auto" }}>
      <ResponsiveChartContainer
        width={width}
        height={chartHeight}
        margin={margin}
        series={series}
        xAxis={[
          {
            id: "labels",
            data: labels,
            scaleType: "band",
          },
        ]}
        yAxis={[
          {
            label: t("Ratio"),
            min: 0,
            tickMinStep: 0.05,
          },
        ]}
        sx={{
          [`& .${axisClasses.directionY} .${axisClasses.line}`]: {
            display: "none",
          },
          [`& .${axisClasses.directionX} .${axisClasses.line}`]: {
            display: "none",
          },
          [`& .${axisClasses.tick}`]: {
            display: "none",
          },
          [`& .${chartsGridClasses.line}`]: {
            strokeDasharray: "4px 4px",
            stroke: "#E5E6EB",
            strokeWidth: "1px",
          },
          "& .MuiBarElement-root": {
            style: "strokeWidth:1;stroke:white",
            // opacity: 0.5,
          },
          flexShrink: 0,
        }}
      >
        <ChartsXAxis />
        <ChartsYAxis
          slots={{
            axisLabel(axisLabelProps, deprecatedLegacyContext) {
              return (
                <text x={-50} y={15}>
                  <tspan> {axisLabelProps.text}</tspan>
                </text>
              );
            },
          }}
        />
        <BarPlot />
        <ChartsReferenceLine
          y={baseLine}
          lineStyle={{
            stroke: "#818181",
            strokeWidth: showBaseLine === true ? baseLineStrokeWidth : 0,
            strokeDasharray: "8 4",
          }}
          labelStyle={{ fill: "#666" }}
        />
        <LinePlot />
        <MarkPlot />
        <ChartsGrid vertical />

        {series.length > 0 && labels.length > 0 && (
          <ChartsTooltip
            slots={{
              axisContent: ({ axisData, series }) => {
                return (
                  <Card
                    sx={{
                      bgcolor: "var(--white)",
                      borderRadius: "8px",
                      padding: "20px",
                      minWidth: "320px",
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <Box borderBottom={"1px solid var(--gray2)"}>
                        <Typography variant="h3" color="secondary">
                          {`${axisData.x?.value ?? ""}`}
                        </Typography>
                      </Box>

                      {series.map((s, i) => {
                        if ("label" in s) {
                          return (
                            <Box
                              borderBottom={"1px solid var(--gray2)"}
                              display={"flex"}
                              flexDirection={"row"}
                              key={i}
                            >
                              <Box
                                key={s.color}
                                display={"inline-flex"}
                                gap={"8px"}
                                alignItems={"center"}
                                flex={1}
                              >
                                <Box
                                  width={"16px"}
                                  height={"16px"}
                                  bgcolor={s.color}
                                  borderRadius={"4px"}
                                />
                                <Typography variant="body1" color="secondary">
                                  {typeof s.label === "string" ? s.label : ""}
                                </Typography>
                              </Box>

                              <Box flex={1} textAlign={"right"}>
                                <Typography variant="body1" color="secondary">
                                  {`${NumberFormatterUtils.numberFormat(
                                    s.data[axisData.x?.index ?? 0] as number,
                                    4,
                                  )}`}
                                </Typography>
                              </Box>

                              <Box flex={1}></Box>
                            </Box>
                          );
                        } else {
                          return "ไม่มี label";
                        }
                      })}
                    </CardContent>
                  </Card>
                );
              },
            }}
          />
        )}
      </ResponsiveChartContainer>
    </Box>
  );
};
